import { ContractListItem } from "@/modules/property-tracks/dtos/contracts/index";
import ContractItem from "./contract-item-list";
import useDownloadGeneratedContracts from "@/modules/property-tracks/hooks/tenants/download-files";
import { Box, Button, Typography, useTheme } from "@mui/material";
import { ArrowDownToLine, Plus } from "lucide-react";
import { EnvironmentVariables } from "@/modules/common/types";

interface GeneratedContractsCardProps {
  contracts: ContractListItem[];
  isFinishingTrack?: boolean;
  propertyUid?: string;
  customerUid?: string;
}

const GeneratedContractsCard = ({
  contracts,
  isFinishingTrack = false,
  propertyUid,
  customerUid,
}: GeneratedContractsCardProps) => {
  const theme = useTheme();
  const { download: downloadContracts, isDownloading: isDownloadingContracts } =
    useDownloadGeneratedContracts();

  const handleDownloadAll = () => {
    if (contracts.length > 0) {
      const contractUids = contracts.map((contract) => contract.uid);
      downloadContracts({ contractUids });
    }
  };

  const handleGenerateContract = () => {
    const contractType = isFinishingTrack ? "Owner" : "Tenant";
    const queryParams = new URLSearchParams({
      contractType,
      propertyUid: propertyUid || "",
    });

    if (customerUid) {
      queryParams.append("clientId", customerUid);
    }

    const contractsUrl = `${
      EnvironmentVariables.CONTRACTS_FRONTEND_URL
    }/contracts?${queryParams.toString()}`;
    window.open(contractsUrl, "_blank");
  };

  const showGenerateButton =
    isFinishingTrack && contracts.length === 0 && propertyUid;

  return (
    <Box
      sx={{
        borderRadius: theme.nawy.radius.lg,
        backgroundColor: "white",
        padding: theme.nawy.spacing.md,
        display: "flex",
        flexDirection: "column",
        gap: theme.nawy.spacing.sm,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
          Generated Contracts
        </Typography>
      </Box>

      {contracts.length > 0 ? (
        <>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {contracts.map((contract) => {
              return <ContractItem key={contract.uid} contract={contract} />;
            })}
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Button
              onClick={handleDownloadAll}
              disabled={isDownloadingContracts}
              variant="contained"
              sx={{
                fontSize: "14px",
                backgroundColor: theme.nawy.colors.primitives.navy[100],
                color: theme.nawy.colors.primitives.blue[500],
                textTransform: "none",
                gap: theme.nawy.spacing.xxs,
                width: "100%",
              }}
            >
              <ArrowDownToLine size={16} />
              Download All
            </Button>
          </Box>
        </>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: theme.nawy.spacing.sm,
            alignItems: "center",
          }}
        >
          <Typography
            variant="caption"
            color={theme.nawy.colors.semantic.text.secondary}
            sx={{
              ...theme.nawy.typography.english.variants.body.md.regular,
            }}
          >
            The property has no generated contracts yet.
          </Typography>
          {showGenerateButton && (
            <Button
              onClick={handleGenerateContract}
              variant="contained"
              sx={{
                fontSize: "14px",
                backgroundColor: theme.nawy.colors.primitives.blue[500],
                color: "white",
                textTransform: "none",
                gap: theme.nawy.spacing.xxs,
                width: "100%",
              }}
            >
              <Plus size={16} />
              Generate Contract
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default GeneratedContractsCard;
